import { useQuery } from "@tanstack/react-query";
import { useTacticHooks } from "../../providers/TacticHooksProvider";

/** React Query key for the releases of one dedicated desk. */
export const dedicatedExceptionsQueryKey = (resourceId: string | undefined) =>
  ["resources.dedicatedExceptions", resourceId] as const;

/** React Query key for the releases on the desks dedicated to a user. */
export const userDedicatedExceptionsQueryKey = (
  userId: string | undefined,
  from?: string
) => ["users.dedicatedExceptions", userId, from ?? ""] as const;

export interface UseDedicatedExceptionsOptions {
  enabled?: boolean;
}

/**
 * The days a dedicated desk is released while its dedicated user is away,
 * earliest first.
 */
export const useDedicatedExceptions = (
  resourceId: string | undefined,
  options?: UseDedicatedExceptionsOptions
) => {
  const { apiClient } = useTacticHooks();
  return useQuery({
    queryKey: dedicatedExceptionsQueryKey(resourceId),
    queryFn: async () => {
      if (!resourceId) return [];
      const response =
        await apiClient.organizations.resourceDedicatedExceptionsGet(resourceId);
      return response.result ?? [];
    },
    enabled: options?.enabled !== false && !!resourceId
  });
};

/**
 * The releases on the desks dedicated to a user from `from` (`yyyy-MM-dd`,
 * default today) on. `me` stands for the signed-in user.
 */
export const useUserDedicatedExceptions = (
  userId: string | undefined,
  from?: string,
  options?: UseDedicatedExceptionsOptions
) => {
  const { apiClient } = useTacticHooks();
  return useQuery({
    queryKey: userDedicatedExceptionsQueryKey(userId, from),
    queryFn: async () => {
      if (!userId) return [];
      const response = await apiClient.organizations.userDedicatedExceptionsGet(
        userId,
        from
      );
      return response.result ?? [];
    },
    enabled: options?.enabled !== false && !!userId
  });
};
